import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../../supabase'

function fmt(n) {
  n = Math.round(n)
  if (n >= 10000000) return '₹' + (n / 10000000).toFixed(1) + ' Cr'
  if (n >= 100000) return '₹' + (n / 100000).toFixed(1) + ' L'
  if (n >= 1000) return '₹' + n.toLocaleString('en-IN')
  return '₹' + n
}

const MEMBERSHIP_LEVELS = [
  { minPct: 80, label: 'Wealth Builder', color: '#c9a84c', bg: 'rgba(201,168,76,0.18)' },
  { minPct: 65, label: 'Game Changer',   color: '#7a5d1e', bg: 'rgba(122,93,30,0.13)' },
  { minPct: 50, label: "Achiever's",     color: '#4a6741', bg: 'rgba(74,103,65,0.13)' },
  { minPct: 30, label: 'Shining Star',   color: '#3d4a5c', bg: 'rgba(61,74,92,0.13)' },
  { minPct: 15, label: 'Debt Crusher',   color: '#b8441a', bg: 'rgba(184,68,26,0.11)' },
  { minPct: 5,  label: 'Budding Star',   color: '#8a5f30', bg: 'rgba(138,95,48,0.12)' },
  { minPct: 0,  label: 'Dreamers',       color: '#8a8278', bg: 'rgba(138,130,120,0.1)' },
]

function getMembership(pct) {
  return MEMBERSHIP_LEVELS.find(l => pct >= l.minPct) || MEMBERSHIP_LEVELS[MEMBERSHIP_LEVELS.length - 1]
}

const roleColors = {
  superadmin: { bg: 'rgba(201,168,76,0.18)', color: '#c9a84c' },
  admin:      { bg: 'rgba(100,140,180,0.15)', color: '#6688aa' },
  gifted:     { bg: 'rgba(201,168,76,0.10)', color: '#c9a84c' },
  user:       { bg: 'rgba(100,160,90,0.15)',  color: '#5f9a52' },
}

export default function AdminUsers({ session, profile }) {
  const [users, setUsers] = useState([])
  const [loansMap, setLoansMap] = useState({})
  const [paymentsMap, setPaymentsMap] = useState({})
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [expandedId, setExpandedId] = useState(null)
  const [toast, setToast] = useState('')
  const [giftModal, setGiftModal] = useState(null) // { userId, userName }
  const [giftMonths, setGiftMonths] = useState(3)
  const [gifting, setGifting] = useState(false)

  const GIFT_MONTH_OPTIONS = [1, 2, 3, 4, 5, 6, 9, 12]
  const now = new Date()

  function showToast(msg) {
    setToast(msg)
    setTimeout(() => setToast(''), 3000)
  }

  const fetchAll = useCallback(async () => {
    const n = new Date()
    const currentMonth = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`
    setLoading(true)
    const [{ data: profilesData }, { data: loansData }, { data: paymentsData }] = await Promise.all([
      supabase.from('profiles').select('*').order('created_at', { ascending: false }),
      supabase.from('loans').select('*'),
      supabase.from('emi_payments').select('*').eq('payment_month', currentMonth),
    ])

    const lm = {}
    const pm = {}
    ;(loansData || []).forEach(l => { lm[l.user_id] = lm[l.user_id] || []; lm[l.user_id].push(l) })
    ;(paymentsData || []).forEach(p => { pm[p.user_id] = pm[p.user_id] || []; pm[p.user_id].push(p) })

    setUsers(profilesData || [])
    setLoansMap(lm)
    setPaymentsMap(pm)
    setLoading(false)
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  async function changeRole(e, userId, newRole) {
    e.stopPropagation()
    if (profile?.role !== 'superadmin') return showToast('Only Super Admin can change roles')
    if (userId === session.user.id) return showToast('You cannot change your own role')
    const { error } = await supabase.from('profiles').update({ role: newRole }).eq('id', userId)
    if (error) showToast('Error: ' + error.message)
    else { showToast('✅ Role updated to ' + newRole); fetchAll() }
  }

  async function toggleActive(e, userId, current) {
    e.stopPropagation()
    const { error } = await supabase.from('profiles').update({ is_active: !current }).eq('id', userId)
    if (error) showToast('Error: ' + error.message)
    else { showToast(current ? '⛔ User deactivated' : '✅ User activated'); fetchAll() }
  }

  async function confirmGift() {
    if (!giftModal) return
    setGifting(true)
    const { userId, userName } = giftModal
    const expiresAt = new Date()
    expiresAt.setMonth(expiresAt.getMonth() + giftMonths)
    const expiryLabel = expiresAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })

    // Update or create memberships row
    const { data: existingMem } = await supabase.from('memberships')
      .select('id').eq('user_id', userId).single()

    const memPayload = {
      is_paid: true,
      gift_expires_at: expiresAt.toISOString(),
      gift_notified: false,
      status: 'active',
      platform: 'gifted',
      tier: 'premium',
      tier_started_at: new Date().toISOString(),
    }

    let memErr
    if (existingMem?.id) {
      // Update existing row
      const { error } = await supabase.from('memberships').update(memPayload).eq('user_id', userId)
      memErr = error
    } else {
      // Insert new row
      const { error } = await supabase.from('memberships').insert({ user_id: userId, ...memPayload })
      memErr = error
    }

    // Update profile role
    const { error: profErr } = await supabase.from('profiles')
      .update({ role: 'gifted' }).eq('id', userId)

    if (memErr || profErr) {
      showToast('❌ Error: ' + (memErr?.message || profErr?.message))
      setGifting(false)
      return
    }

    // Insert notification for user
    await supabase.from('notifications').insert({
      user_id: userId,
      title: '🎁 Premium Access Gifted!',
      message: `You have been gifted DebtCrush Premium for ${giftMonths} month${giftMonths > 1 ? 's' : ''}! Enjoy all Premium features until ${expiryLabel}.`,
      type: 'gift_premium',
      is_read: false,
    })

    setGifting(false)
    setGiftModal(null)
    showToast(`🎁 Premium gifted to ${userName} for ${giftMonths} months!`)
    // Optimistic update
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: 'gifted' } : u))
    fetchAll()
  }

  async function revokeGift(e, userId, userName) {
    e.stopPropagation()
    const { error: memErr } = await supabase.from('memberships')
      .update({ is_paid: false, gift_expires_at: null, gift_notified: false, status: 'inactive' })
      .eq('user_id', userId)
    const { error: profErr } = await supabase.from('profiles')
      .update({ role: 'user' }).eq('id', userId)
    if (memErr || profErr) {
      showToast('❌ Error: ' + (memErr?.message || profErr?.message))
      return
    }
    await supabase.from('notifications').insert({
      user_id: userId,
      title: 'Premium Access Ended',
      message: 'Your gifted DebtCrush Premium access has been revoked. You are now on the Free plan.',
      type: 'gift_revoked',
      is_read: false,
    })
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: 'user' } : u))
    showToast(`✅ Gift revoked for ${userName}`)
    fetchAll()
  }

  function userStats(userId) {
    const loans = loansMap[userId] || []
    const payments = paymentsMap[userId] || []
    const totalDebt = loans.reduce((s, l) => s + parseFloat(l.balance || 0), 0)
    const totalJoining = loans.reduce((s, l) => s + parseFloat(l.joining_balance || l.original_amount || l.balance || 0), 0)
    const debtPct = totalJoining > 0 ? Math.min(100, Math.max(0, Math.round(((totalJoining - totalDebt) / totalJoining) * 100))) : 0
    const applicable = loans.filter(l => { const p = payments.find(p => p.loan_id === l.id); return !p || p.status !== 'na' })
    const paid = payments.filter(p => p.status === 'paid')
    const compliance = applicable.length > 0 ? Math.round((paid.length / applicable.length) * 100) : null
    return { loans, payments, totalDebt, totalJoining, debtPct, compliance, applicable, paid, membership: getMembership(debtPct) }
  }

  const allStats = useMemo(() => {
    const map = {}
    users.forEach(u => { map[u.id] = userStats(u.id) })
    return map
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, loansMap, paymentsMap])

  const summary = useMemo(() => {
    const totalDebt = users.reduce((s, u) => s + (allStats[u.id]?.totalDebt || 0), 0)
    const debtPcts = users.map(u => allStats[u.id]?.debtPct || 0)
    const avgPct = users.length > 0 ? Math.round(debtPcts.reduce((s, p) => s + p, 0) / users.length) : 0
    const activeCount = users.filter(u => (paymentsMap[u.id] || []).length > 0).length
    return { totalDebt, avgPct, activeCount }
  }, [users, allStats, paymentsMap])

  function exportCSV() {
    const header = ['Name', 'Phone', 'Email', 'Join Date', 'Membership', 'Loans', 'Total Debt (₹)', 'Debt Reduced %', 'EMI Compliance %', 'Status', 'Last Active']
    const rows = users.map(u => {
      const s = allStats[u.id] || {}
      return [
        u.full_name || 'Unknown',
        u.phone || u.phone_number || '',
        u.email || '',
        new Date(u.created_at).toLocaleDateString('en-IN'),
        s.membership?.label || 'Dreamers',
        s.loans?.length || 0,
        Math.round(s.totalDebt || 0),
        s.debtPct || 0,
        s.compliance !== null && s.compliance !== undefined ? s.compliance : '',
        u.is_active ? 'Active' : 'Inactive',
        (u.updated_at || u.created_at) ? new Date(u.updated_at || u.created_at).toLocaleDateString('en-IN') : '',
      ]
    })
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `debtcrush-users-${now.toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const filtered = users.filter(u =>
    (u.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.phone || '').includes(search) ||
    (u.phone_number || '').includes(search) ||
    (u.email || '').toLowerCase().includes(search.toLowerCase())
  )

  const COL = '36px 2fr 1.2fr 1fr 1fr 1fr 0.9fr 0.8fr'

  return (
    <div>
      {toast && (
        <div style={{
          position: 'fixed', bottom: '1.5rem', right: '1.5rem',
          background: '#0d0d0d', color: '#f5f0e8',
          padding: '0.7rem 1.1rem', borderRadius: '6px',
          fontSize: '0.8rem', zIndex: 9999, borderLeft: '3px solid #c9a84c'
        }}>{toast}</div>
      )}

      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '1.55rem', fontWeight: '700', margin: 0 }}>User Management</h2>
        <p style={{ color: '#8a8278', fontSize: '0.8rem', marginTop: '0.3rem' }}>View and manage all DebtCrush users.</p>
      </div>

      {/* Summary Stats */}
      {!loading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
          {[
            { label: 'Total Users',        value: users.length,        icon: '👥', color: '#3d4a5c' },
            { label: 'Total Debt Tracked', value: fmt(summary.totalDebt), icon: '💰', color: '#b8441a' },
            { label: 'Avg Debt Reduction', value: summary.avgPct + '%',  icon: '📉', color: '#4a6741' },
            { label: 'Active This Month',  value: summary.activeCount,  icon: '⚡', color: '#c9a84c' },
          ].map((c, i) => (
            <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.1rem 1.3rem', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: c.color }} />
              <div style={{ fontSize: '1.2rem', marginBottom: '0.3rem' }}>{c.icon}</div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>{c.label}</div>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.55rem', fontWeight: '700', color: 'var(--text)' }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>

        {/* Header bar */}
        <div style={{ padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.8rem', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>All Users ({filtered.length})</span>
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              placeholder="Search by name, phone or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: '4px', padding: '0.4rem 0.7rem', fontSize: '0.8rem', fontFamily: 'sans-serif', outline: 'none', width: '220px' }}
            />
            <button
              onClick={exportCSV}
              style={{ background: '#0d0d0d', color: '#f5f0e8', border: 'none', borderRadius: '4px', padding: '0.42rem 0.9rem', cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}
            >
              ↓ Export CSV
            </button>
          </div>
        </div>

        {/* Column headers */}
        {!loading && filtered.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: COL, padding: '0.5rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)', gap: '0.5rem', alignItems: 'center' }}>
            <div />
            {['User', 'Membership', 'Total Debt', 'Debt Reduced', 'EMI Compliance', 'Last Active', 'Status'].map(h => (
              <div key={h} style={{ fontFamily: 'monospace', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>{h}</div>
            ))}
          </div>
        )}

        {loading ? (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading users...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>No users found.</div>
        ) : (
          filtered.map((user, i) => {
            const s = allStats[user.id] || {}
            const isExp = expandedId === user.id
            const rc = roleColors[user.role] || roleColors.user
            const lastActive = user.updated_at || user.created_at

            return (
              <div key={user.id} style={{ borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none' }}>

                {/* Main row */}
                <div
                  onClick={() => setExpandedId(isExp ? null : user.id)}
                  style={{
                    display: 'grid', gridTemplateColumns: COL, alignItems: 'center',
                    padding: '0.85rem 1.4rem', gap: '0.5rem', cursor: 'pointer',
                    background: isExp ? 'rgba(201,168,76,0.06)' : 'transparent',
                    borderLeft: isExp ? '3px solid #c9a84c' : '3px solid transparent',
                    transition: 'background 0.15s, border-color 0.15s',
                  }}
                  onMouseEnter={e => { if (!isExp) e.currentTarget.style.background = 'rgba(201,168,76,0.04)' }}
                  onMouseLeave={e => { if (!isExp) e.currentTarget.style.background = 'transparent' }}
                >
                  {/* Avatar */}
                  <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: user.is_active ? '#0d0d0d' : 'var(--border)', color: '#f5f0e8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: '700', flexShrink: 0 }}>
                    {(user.full_name || 'U').charAt(0).toUpperCase()}
                  </div>

                  {/* Name + contact */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: '600', fontSize: '0.82rem', lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.full_name || 'Unknown'}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {user.phone || user.phone_number || 'No phone'}
                      {user.email ? <span style={{ marginLeft: '0.4rem', opacity: 0.8 }}>· {user.email}</span> : null}
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: 'var(--text-muted)' }}>
                      Joined {new Date(user.created_at).toLocaleDateString('en-IN')}
                    </div>
                  </div>

                  {/* Membership badge */}
                  <div>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', padding: '0.18rem 0.45rem', borderRadius: '3px', background: s.membership?.bg, color: s.membership?.color, fontWeight: '700', whiteSpace: 'nowrap' }}>
                      {s.membership?.label || 'Dreamers'}
                    </span>
                    <span style={{ marginLeft: '0.4rem', fontFamily: 'monospace', fontSize: '0.58rem', padding: '0.15rem 0.4rem', borderRadius: '3px', background: rc.bg, color: rc.color, fontWeight: '500', textTransform: 'uppercase' }}>{user.role || 'user'}</span>
                  </div>

                  {/* Total debt */}
                  <div>
                    <div style={{ fontFamily: 'Georgia,serif', fontSize: '0.88rem', fontWeight: '700', color: s.totalDebt > 0 ? '#b8441a' : '#4a6741' }}>
                      {(s.loans || []).length > 0 ? fmt(s.totalDebt) : '—'}
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: 'var(--text-muted)' }}>{(s.loans || []).length} loan{(s.loans || []).length !== 1 ? 's' : ''}</div>
                  </div>

                  {/* Debt reduction */}
                  <div>
                    {(s.loans || []).length > 0 ? (
                      <>
                        <div style={{ fontFamily: 'Georgia,serif', fontSize: '0.88rem', fontWeight: '700', color: '#4a6741' }}>{s.debtPct}%</div>
                        <div style={{ background: 'var(--border)', borderRadius: '2px', height: '3px', marginTop: '4px', width: '55px' }}>
                          <div style={{ background: '#4a6741', height: '3px', borderRadius: '2px', width: Math.min(100, s.debtPct) + '%' }} />
                        </div>
                      </>
                    ) : <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>—</span>}
                  </div>

                  {/* EMI compliance */}
                  <div>
                    {s.compliance !== null && s.compliance !== undefined ? (
                      <>
                        <div style={{ fontFamily: 'Georgia,serif', fontSize: '0.88rem', fontWeight: '700', color: s.compliance >= 80 ? '#4a6741' : s.compliance >= 50 ? '#c9a84c' : '#b8441a' }}>
                          {s.compliance}%
                        </div>
                        <div style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: 'var(--text-muted)' }}>
                          {(s.paid || []).length}/{(s.applicable || []).length} paid
                        </div>
                      </>
                    ) : <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>—</span>}
                  </div>

                  {/* Last active */}
                  <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                    {lastActive ? new Date(lastActive).toLocaleDateString('en-IN') : '—'}
                  </div>

                  {/* Status + expand toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <span style={{
                      fontFamily: 'monospace', fontSize: '0.58rem', padding: '0.15rem 0.4rem', borderRadius: '3px',
                      background: user.is_active ? 'rgba(74,103,65,0.12)' : 'rgba(184,68,26,0.1)',
                      color: user.is_active ? '#4a6741' : '#b8441a', fontWeight: '600', textTransform: 'uppercase'
                    }}>{user.is_active ? 'Active' : 'Inactive'}</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.65rem' }}>{isExp ? '▲' : '▼'}</span>
                  </div>
                </div>

                {/* Expanded detail panel */}
                {isExp && (
                  <div style={{ padding: '1.3rem 1.6rem', background: 'var(--bg)', borderTop: '1px solid var(--border)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '1.4rem' }}>

                      {/* Left: user details + controls */}
                      <div>
                        <div style={{ fontFamily: 'monospace', fontSize: '0.63rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.8rem' }}>User Details</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.7rem 1rem', marginBottom: '1.1rem' }}>
                          {[
                            ['Full Name', user.full_name || 'Unknown'],
                            ['Phone', user.phone || user.phone_number || 'N/A'],
                            ['Email', user.email || 'N/A'],
                            ['Join Date', new Date(user.created_at).toLocaleDateString('en-IN')],
                            ['Last Active', lastActive ? new Date(lastActive).toLocaleDateString('en-IN') : 'N/A'],
                            ['Membership', s.membership?.label || 'Dreamers'],
                            ['Total Loans', (s.loans || []).length],
                            ['Total Debt', (s.loans || []).length > 0 ? fmt(s.totalDebt) : 'No loans'],
                            ['Debt Reduced', (s.loans || []).length > 0 ? s.debtPct + '%' : '—'],
                            ['EMI Compliance', s.compliance !== null && s.compliance !== undefined ? s.compliance + '%' : '—'],
                          ].map(([label, value]) => (
                            <div key={label}>
                              <div style={{ fontFamily: 'monospace', fontSize: '0.58rem', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.15rem' }}>{label}</div>
                              <div style={{ fontSize: '0.8rem', fontWeight: '500' }}>{value}</div>
                            </div>
                          ))}
                        </div>

                        {/* Role change (superadmin only) */}
                        {profile?.role === 'superadmin' && user.id !== session.user.id && (
                          <div style={{ marginBottom: '0.9rem' }}>
                            <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.45rem' }}>Change Role</div>
                            <div style={{ display: 'flex', gap: '0.4rem' }}>
                              {['user', 'admin', 'superadmin'].map(role => (
                                <button
                                  key={role}
                                  onClick={e => changeRole(e, user.id, role)}
                                  style={{
                                    padding: '0.28rem 0.65rem', borderRadius: '3px', border: '1px solid', cursor: 'pointer',
                                    fontFamily: 'monospace', fontSize: '0.6rem', textTransform: 'uppercase', fontWeight: '500',
                                    borderColor: user.role === role ? 'var(--btn-bg)' : 'var(--border)',
                                    background: user.role === role ? 'var(--btn-bg)' : 'transparent',
                                    color: user.role === role ? 'var(--btn-text)' : 'var(--text-muted)',
                                  }}
                                >{role}</button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Activate/deactivate */}
                        {user.id !== session.user.id && (
                          <button
                            onClick={e => toggleActive(e, user.id, user.is_active)}
                            style={{
                              padding: '0.38rem 0.85rem', borderRadius: '4px', border: '1px solid', cursor: 'pointer',
                              fontFamily: 'monospace', fontSize: '0.65rem', textTransform: 'uppercase',
                              borderColor: user.is_active ? '#b8441a' : '#4a6741',
                              color: user.is_active ? '#b8441a' : '#4a6741',
                              background: 'transparent',
                            }}
                          >{user.is_active ? '⛔ Deactivate User' : '✅ Activate User'}</button>
                        )}

                        {/* Gift Premium — superadmin only, not for admins/superadmins */}
                        {profile?.role === 'superadmin' && user.id !== session.user.id && user.role !== 'admin' && user.role !== 'superadmin' && (
                          <div style={{ marginTop: '0.9rem' }}>
                            <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.45rem' }}>
                              Premium Access
                            </div>
                            {user.role === 'gifted' ? (
                              <div>
                                <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#c9a84c', marginBottom: '0.4rem' }}>
                                  🎁 Gifted Premium · Active
                                </div>
                                <button
                                  onClick={e => revokeGift(e, user.id, user.full_name || 'User')}
                                  style={{ padding: '0.38rem 0.85rem', borderRadius: '4px', border: '1px solid #b8441a', cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.65rem', textTransform: 'uppercase', color: '#b8441a', background: 'rgba(184,68,26,0.06)' }}
                                >
                                  🎁 Revoke Gift Access
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={e => { e.stopPropagation(); setGiftMonths(3); setGiftModal({ userId: user.id, userName: user.full_name || 'User' }) }}
                                style={{ padding: '0.38rem 0.85rem', borderRadius: '4px', border: '1px solid #c9a84c', cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.65rem', textTransform: 'uppercase', color: '#c9a84c', background: 'rgba(201,168,76,0.08)' }}
                              >
                                🎁 Gift Premium Access
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Right: loans list */}
                      <div>
                        <div style={{ fontFamily: 'monospace', fontSize: '0.63rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.8rem' }}>
                          Loans ({(s.loans || []).length})
                        </div>
                        {(s.loans || []).length === 0 ? (
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontStyle: 'italic' }}>No loans added yet.</div>
                        ) : (
                          <div style={{ border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden', background: 'var(--surface)' }}>
                            {s.loans.map((loan, li) => {
                              const orig = parseFloat(loan.original_amount || loan.balance)
                              const bal = parseFloat(loan.balance)
                              const lPct = orig > 0 ? Math.min(100, Math.max(0, Math.round(((orig - bal) / orig) * 100))) : 0
                              return (
                                <div
                                  key={loan.id}
                                  style={{ padding: '0.7rem 1rem', borderBottom: li < s.loans.length - 1 ? '1px solid var(--border)' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}
                                >
                                  <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: '600', fontSize: '0.8rem', marginBottom: '0.15rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{loan.name}</div>
                                    <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)' }}>{loan.interest_rate}% p.a. · EMI {fmt(loan.emi_amount)}/mo</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.3rem' }}>
                                      <div style={{ background: 'var(--border)', borderRadius: '2px', height: '3px', width: '60px' }}>
                                        <div style={{ background: '#4a6741', height: '3px', borderRadius: '2px', width: lPct + '%' }} />
                                      </div>
                                      <span style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: '#4a6741' }}>{lPct}% reduced</span>
                                    </div>
                                  </div>
                                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                    <div style={{ fontFamily: 'Georgia,serif', fontWeight: '700', fontSize: '0.88rem', color: '#b8441a' }}>{fmt(bal)}</div>
                                    <div style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: 'var(--text-muted)' }}>of {fmt(orig)}</div>
                                  </div>
                                </div>
                              )
                            })}
                            <div style={{ padding: '0.6rem 1rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--surface-hdr)' }}>
                              <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Total Remaining Debt</span>
                              <span style={{ fontFamily: 'Georgia,serif', fontSize: '1rem', fontWeight: '700', color: '#b8441a' }}>{fmt(s.totalDebt)}</span>
                            </div>
                          </div>
                        )}
                      </div>

                    </div>
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
      {/* Gift Month Selector Modal */}
      {giftModal && (
        <div
          onClick={() => !gifting && setGiftModal(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--surface)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '12px', width: '100%', maxWidth: '420px', padding: '1.75rem', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
          >
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.2rem', fontWeight: '700', marginBottom: '0.3rem' }}>
              🎁 Gift Premium Access
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '1.25rem' }}>
              Gifting to: <strong style={{ color: 'var(--text)' }}>{giftModal.userName}</strong>
            </div>

            <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.6rem' }}>
              Select Duration
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1.25rem' }}>
              {GIFT_MONTH_OPTIONS.map(m => (
                <button
                  key={m}
                  onClick={() => setGiftMonths(m)}
                  style={{
                    padding: '0.45rem 0.9rem', borderRadius: '6px', border: '1.5px solid', cursor: 'pointer',
                    fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: '600',
                    borderColor: giftMonths === m ? '#c9a84c' : 'var(--border)',
                    background: giftMonths === m ? 'rgba(201,168,76,0.12)' : 'transparent',
                    color: giftMonths === m ? '#c9a84c' : 'var(--text-muted)',
                    transition: 'all 0.15s',
                  }}
                >
                  {m} {m === 1 ? 'Month' : 'Months'}
                </button>
              ))}
            </div>

            {/* Preview */}
            <div style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1.25rem' }}>
              <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>Gift Summary</div>
              <div style={{ fontSize: '0.85rem', fontWeight: '600' }}>
                {giftModal.userName} gets Premium for <span style={{ color: '#c9a84c' }}>{giftMonths} month{giftMonths > 1 ? 's' : ''}</span>
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                Expires: {(() => { const d = new Date(); d.setMonth(d.getMonth() + giftMonths); return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) })()}
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: '#4a6741', marginTop: '0.25rem' }}>
                ✓ User will be notified via in-app notification
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button
                onClick={confirmGift}
                disabled={gifting}
                style={{ flex: 1, background: '#c9a84c', color: '#0d0d0d', border: 'none', padding: '0.6rem', borderRadius: '6px', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '700', cursor: gifting ? 'not-allowed' : 'pointer', opacity: gifting ? 0.6 : 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}
              >
                {gifting ? '⏳ Gifting...' : '✓ Confirm Gift'}
              </button>
              <button
                onClick={() => !gifting && setGiftModal(null)}
                style={{ padding: '0.6rem 1rem', background: 'transparent', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
